// The shared analytics range's control (M7 slice 7; src/workspace/analyticsRange.ts): presets 7 / 14 / 30 / 90 days and a
// custom from–to of at most 92 days, optionally the "Include testers" switch. Every page that reads the counters shows the
// same range, remembered per user.
import { useEffect, useState } from 'react';
import { useLiveOpsEnv } from '../../content/liveops/env';
import { Button } from '../../ui/Button';
import { Switch } from '../../ui/inputs';
import { Tabs } from '../../ui/Tabs';
import { PRESETS, useAnalyticsRange } from '../../workspace/analyticsRange';

/** One line: the presets as a segmented control (7 d … 90 d, Custom), the two dates and Apply when custom, the range text. */
export function RangePicker({ testers = false }: { testers?: boolean }) {
  const { env } = useLiveOpsEnv();
  const { range, setPreset, setCustom, setIncludeTesters } = useAnalyticsRange(env);
  const [custom, setCustomOpen] = useState(range.preset === 'custom');
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    setFrom(range.from);
    setTo(range.to);
  }, [range.from, range.to]);
  return (
    <span className="range-picker lo-range" data-testid="analytics-range" title="The analytics range: the days the counters cover (game-time days of the selected environment), remembered per user">
      <Tabs
        segmented
        ariaLabel="Analytics range"
        value={custom ? 'custom' : String(range.preset)}
        onChange={(key) => {
          if (key === 'custom') {
            setCustomOpen(true);
            return;
          }
          setCustomOpen(false);
          setError(null);
          setPreset(Number(key) as (typeof PRESETS)[number]);
        }}
        items={[...PRESETS.map((p) => ({ key: String(p), label: `${p} d` })), { key: 'custom', label: 'Custom' }]}
      />
      {custom && (
        <span className="lo-range-custom">
          <input type="date" className="input input-s" aria-label="Range from" value={from} onChange={(e) => setFrom(e.target.value)} />
          <span className="lo-range-dash" aria-hidden="true">–</span>
          <input type="date" className="input input-s" aria-label="Range to" value={to} onChange={(e) => setTo(e.target.value)} />
          <Button size="s" onClick={() => setError(setCustom(from, to))}>
            Apply
          </Button>
          {error && (
            <span className="lo-note danger" role="alert">
              {error}
            </span>
          )}
        </span>
      )}
      <span className="lo-range-text" data-testid="analytics-range-text">
        {range.from} → {range.to}
      </span>
      {testers && <Switch label="Include testers" checked={range.includeTesters} onChange={setIncludeTesters} />}
    </span>
  );
}
